import dayjs from 'dayjs';
import { BarData, BusinessDay, LineData, Time, isBusinessDay } from 'lightweight-charts';

import { EChartPeriod, IMarketChartData } from '@webtrader/core/api/market/chart';
import { EChartLineSeriesDataType, ICustomBarData } from '@webtrader/core/constants/chart';
import { safetyCheckForNumberValues } from '@webtrader/core/format/symbol';
import { companyService } from '@webtrader/core/services/company';
import { destructureAsNumber } from '@webtrader/core/utils';

export interface IPeriodToDatesParams {
  period: EChartPeriod;
  latestTo?: number;
  latestFrom?: number;
  dateToUsesEndOfDay?: boolean;
}

// Separate functions from dayjs core library
export const timeToDayjs = (time?: Time) => {
  const offset = companyService.getTimezoneOffset() ?? 0;

  return dayjs(time ? timeToDate(time) : undefined).utcOffset(offset);
};

export const timeToDate = (time: Time) => {
  if (isBusinessDay(time)) {
    const date = time as BusinessDay;

    return new Date(date.year, date.month, date.day);
  }

  if (typeof time === 'string') {
    return new Date(time);
  }

  return new Date(time * 1000);
};

const formatChartDataValue = (value: number, decimal?: number) => {
  if (decimal && safetyCheckForNumberValues(value)) {
    return Number(value.toFixed(decimal));
  }
  return safetyCheckForNumberValues(value);
};

export const rawToBarData = (chartData: IMarketChartData[], decimal?: number) => {
  const barData: ICustomBarData[] = [];

  chartData.forEach((d) => {
    const { chartTime, ask, open, close, high, low } = destructureAsNumber(d, [
      'chartTime',
      'ask',
      'open',
      'close',
      'high',
      'low'
    ]);

    barData.push({
      time: (chartTime / 1000) as Time,
      ask: formatChartDataValue(ask, decimal),
      open: formatChartDataValue(open, decimal),
      close: formatChartDataValue(close, decimal),
      high: formatChartDataValue(high, decimal),
      low: formatChartDataValue(low, decimal)
    });
  });

  return barData;
};

export const barToLineData = (barData: BarData): LineData => {
  return {
    time: barData.time,
    value: barData.close
  };
};

export const barToLineSeriesData = (dataType: EChartLineSeriesDataType, barData: ICustomBarData): LineData => {
  return {
    time: barData.time,
    value: dataType === EChartLineSeriesDataType.ASK ? barData.ask ?? 0 : barData.close
  };
};

// eslint-disable-next-line complexity
export const periodToDates = (params: IPeriodToDatesParams) => {
  const { period, latestTo, latestFrom, dateToUsesEndOfDay } = params;

  const to = dateToUsesEndOfDay
    ? timeToDayjs(latestTo as Time)
        .endOf('day')
        .toDate()
        .getTime()
    : timeToDayjs(latestTo as Time)
        .toDate()
        .getTime();
  const from = timeToDayjs(latestFrom as Time);

  switch (period) {
    case EChartPeriod.MINUTE_5:
      return {
        to,
        from: from.subtract(3, 'days').toDate().getTime()
      };
    case EChartPeriod.MINUTE_10:
      return {
        to,
        from: from.subtract(3, 'days').toDate().getTime()
      };
    case EChartPeriod.MINUTE_15:
      return {
        to,
        from: from.subtract(3, 'days').toDate().getTime()
      };
    case EChartPeriod.MINUTE_20:
      return {
        to,
        from: from.subtract(7, 'days').toDate().getTime()
      };
    case EChartPeriod.MINUTE_30:
      return {
        to,
        from: from.subtract(7, 'days').toDate().getTime()
      };
    case EChartPeriod.HOUR_1:
      return {
        to,
        from: from.subtract(1, 'month').toDate().getTime()
      };
    case EChartPeriod.HOUR_2:
      return {
        to,
        from: from.subtract(2, 'months').toDate().getTime()
      };
    case EChartPeriod.HOUR_3:
      return {
        to,
        from: from.subtract(3, 'months').toDate().getTime()
      };
    case EChartPeriod.HOUR_4:
      return {
        to,
        from: from.subtract(4, 'months').toDate().getTime()
      };
    case EChartPeriod.HOUR_6:
      return {
        to,
        from: from.subtract(6, 'months').toDate().getTime()
      };
    case EChartPeriod.DAY:
      return {
        to,
        from: from.subtract(1, 'year').toDate().getTime()
      };
    case EChartPeriod.WEEK:
      return {
        to,
        from: from.subtract(6, 'years').toDate().getTime()
      };
    case EChartPeriod.MONTH:
      return {
        to,
        from: from.subtract(12, 'years').toDate().getTime()
      };
    default:
      return {
        to,
        from: from.subtract(3, 'days').toDate().getTime()
      };
  }
};

export const trimBarData = (barData: ICustomBarData[], limit: number) => {
  const trimmedBarData = [...barData];
  let filteredData = false;
  while (!filteredData) {
    const newLastData = trimmedBarData[trimmedBarData.length - 1];
    if (!newLastData) {
      filteredData = true;
      break;
    }
    if (Number(newLastData.time) >= limit) {
      trimmedBarData.pop();
    } else {
      filteredData = true;
    }
  }
  return trimmedBarData;
};
